import Button from '@carvana/ds-button';
import { ZapFilledCvna } from '@carvana/ds-icons';
import { Check } from 'lucide-react';
import { icon } from '@carvana/ds-styles';
import React from 'react';
import EventedModal from '|/app/modals/evented-modal';
import { ModalNames } from '|/app/modals/modal-events';
import { StepName } from '|/app/services';
import useNavigateToStep from '|/hooks/use-navigate-to-step';
import styles from './carvana-care-modal.module.css';
import getCoverageFeatures, { getFuelType, FUEL_SPECIFIC_ADDITIONS } from './get-coverage-features';

export interface CarvanaCareModalProps {
  isOpen: boolean;
  setIsOpen: React.Dispatch<React.SetStateAction<boolean>>;
  coverageType: 'Essential' | 'Cosmetic' | 'Plus' | 'Premier';
  coverageDetails: {
    duration: string;
    mileage: string;
  };
  fuelDescription?: string;
}

const CarvanaCareModal = ({
  isOpen,
  setIsOpen,
  coverageType,
  coverageDetails,
  fuelDescription
}: CarvanaCareModalProps) => {
  const navigate = useNavigateToStep();
  const fuelType = getFuelType(fuelDescription);
  const features = getCoverageFeatures(coverageType, fuelType);

  // Helper function to determine if a feature is EV/Hybrid specific
  const isElectricOrHybridFeature = (feature: string): boolean => {
    const evHybridFeatures = [
      ...FUEL_SPECIFIC_ADDITIONS.electric,
      ...FUEL_SPECIFIC_ADDITIONS.hybrid
    ];
    return evHybridFeatures.includes(feature);
  };

  return (
    <EventedModal
      modalHeader="CarvanaCare®"
      mobileStyle="inset"
      desktopWidth="480px"
      divLine={false}
      onClose={() => {
        setIsOpen(false);
      }}
      isOpen={isOpen}
      analyticsId={ModalNames.CarvanaCare}
      contentPadding={false}
    >
      <div className={styles.container} data-testid="carvana-care-modal-container">
        <div className={styles.content}>
          <div className={styles.coverageSection}>
            <div className={styles.headline}>
              <h2 className={styles.title}>
                {(fuelType === 'electric' || fuelType === 'hybrid') && (
                  <ZapFilledCvna
                    size={16}
                    color={icon.primary}
                    className={styles.titleIcon}
                    data-testid="title-zap-icon"
                  />
                )}
                {coverageType}
              </h2>
            </div>
            <h3
              className={styles.coverageDescription}
              data-testid="carvana-care-coverage-description"
            >
              You&apos;re covered with CarvanaCare {coverageType} for{' '}
              <strong>
                {coverageType === 'Cosmetic'
                  ? coverageDetails.duration
                  : `${coverageDetails.duration} or ${coverageDetails.mileage}`}
              </strong>
            </h3>
          </div>

          <div className={styles.featuresSection}>
            <ul className={styles.featuresList} data-testid="carvana-care-features-list">
              {features.map(feature => {
                const isEvHybridFeature = isElectricOrHybridFeature(feature);
                const IconComponent = isEvHybridFeature ? ZapFilledCvna : Check;
                const iconTestId = isEvHybridFeature ? 'zap-icon' : 'check-icon';
                const featureId = feature.replace(/[^a-zA-Z0-9]/g, '-').toLowerCase();

                return (
                  <li
                    key={featureId}
                    className={styles.featureItem}
                    data-testid={`carvana-care-feature-${featureId}`}
                  >
                    <IconComponent
                      size={16}
                      color={icon.primary}
                      className={styles.checkIcon}
                      data-testid={iconTestId}
                    />
                    <span className={styles.featureText}>{feature}</span>
                  </li>
                );
              })}
            </ul>
          </div>
        </div>

        <div className={styles.actions}>
          <Button
            variant="tertiary"
            size="md"
            data-testid="edit-carvana-care-button"
            data-analytics-id="pp_carvana_care_modal_edit"
            onClick={() => {
              navigate(StepName.VSC);
            }}
          >
            Edit
          </Button>
          <Button
            variant="primary"
            size="md"
            data-testid="close-carvana-care-button"
            data-analytics-id="pp_carvana_care_modal_close"
            onClick={() => {
              setIsOpen(false);
            }}
          >
            Close
          </Button>
        </div>
      </div>
    </EventedModal>
  );
};

export default CarvanaCareModal;
